import styled, { css, keyframes } from "styled-components";
import { PRIMARY, WHITE } from "../../utils/colors";
import device from "../../utils/mediaQuery";
import { StyledParagraph } from "../CustomAntdComponents/Typography";

/* ───── keyframes ───── */

const pulseKeyframe = keyframes`
  0%   { transform: scale(1);    opacity: 1;   }
  50%  { transform: scale(1.03); opacity: 0.85; }
  100% { transform: scale(1);    opacity: 1;   }
`;

const floatKeyframe = keyframes`
  0%   { transform: translateY(25px); opacity: 0; }
  25%  { opacity: 0.8; }
  50%  { transform: translateY(-25px); opacity: 0; }
  75%  { opacity: 0.8; }
  100% { transform: translateY(25px); opacity: 0; }
`;

const shimmerKeyframe = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const fadeStepKeyframe = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0);   }
`;

/* ───── layout ───── */

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  background: linear-gradient(180deg, #fdf6ee 0%, #fefbf7 100%);
`;

export const Content = styled.div`
  text-align: center;
  width: 100%;
  max-width: 400px;
  padding: 0 16px;
`;

export const ContentContainer = styled.div`
  position: relative;
  height: 400px;
  display: flex;
  justify-content: center;
  width: 100%;
`;

/* ───── step transition ───── */

export const StepContainer = styled.div<{ active: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: ${({ active }) => (active ? 1 : 0)};
  visibility: ${({ active }) => (active ? "visible" : "hidden")};
  transition: opacity 0.4s ease-in-out, visibility 0.4s ease-in-out;
  width: 100%;

  ${({ active }) =>
    active &&
    css`
      animation: ${fadeStepKeyframe} 0.4s ease-out;
    `}
`;

/* ────────────────────────────────────────────────
   STEP 1 — Flight info card
   ──────────────────────────────────────────────── */

export const InfoWrapper = styled.div`
  width: 240px;
  height: 220px;
  padding: 28px 20px 0;
  background: linear-gradient(
    180deg,
    ${PRIMARY} 0%,
    rgba(255, 200, 120, 0.7) 70%,
    rgba(255, 255, 255, 0.4) 100%
  );
  border-radius: 24px;
  margin: 0 auto 0;
  position: relative;
  overflow: hidden;
  animation: ${pulseKeyframe} 1.6s ease-in-out infinite;

  .ant-typography {
    color: ${WHITE};
  }
`;

export const AirportInfo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 16px;
  margin-bottom: 4px;

  svg {
    width: 24px;
    height: 24px;

    path {
      fill: ${WHITE} !important;
    }
  }
`;

export const AirportItem = styled.div`
  max-width: 80px;

  .ant-typography {
    color: ${WHITE};
    text-transform: capitalize;
  }

  ${StyledParagraph} {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 11px;
    margin-bottom: 0;
  }

  h1.ant-typography {
    font-size: 28px;
    line-height: 1.1;
    margin-bottom: 0;
  }
`;

export const TextWrapper = styled.div`
  margin-bottom: 4px;

  .ant-typography {
    font-size: 12px;
    color: ${WHITE};
    opacity: 0.9;
  }
`;

/* Grid of small squares at bottom of card */
export const BoxesWrapper = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 80px;
  padding: 6px 8px;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 3px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 0 0 24px 24px;
`;

export const BoxItem = styled.div<{ index: number }>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: ${WHITE};
  opacity: 0.15;

  /* Highlight one box to match Figma */
  ${({ index }) =>
    index === 15 &&
    css`
      opacity: 0.6;
    `}
`;

/* ────────────────────────────────────────────────
   STEP 2 — Skeleton / code preview
   ──────────────────────────────────────────────── */

export const DashedBox = styled.div`
  width: 90%;
  max-width: 380px;
  height: 300px;
  border: 1.5px dashed ${PRIMARY};
  border-radius: 24px;
  background: ${WHITE};
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 32px 28px;
  gap: 16px;
  opacity: 0.9;
`;

/**
 * Skeleton lines — dashed style matching the box border,
 * not solid fills. Each line is a dashed border rectangle
 * with a subtle shimmer.
 */
export const SkeletonLine = styled.div<{ width: string; height?: string }>`
  width: ${({ width }) => width};
  height: ${({ height }) => height || "16px"};
  border: 1.5px dashed ${PRIMARY};
  border-radius: 8px;
  opacity: 0.4;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(232, 116, 12, 0.08) 50%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: ${shimmerKeyframe} 2s ease-in-out infinite;
  }
`;

export const CodeIconWrapper = styled.div`
  color: ${PRIMARY};
  font-size: 14px;
  font-weight: 500;
  border: 1.5px dashed ${PRIMARY};
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.4;
`;

/* ────────────────────────────────────────────────
   STEP 3 — Floating travel icons
   ──────────────────────────────────────────────── */

export const FloatingIconWrapper = styled.div<{
  top: string;
  left: string;
  delay: string;
  fontSize?: string;
}>`
  position: absolute;
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  color: ${PRIMARY};
  font-size: ${({ fontSize }) => fontSize || "24px"};
  animation: ${floatKeyframe} 3s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay};
  opacity: 0;
`;

/* ────────────────────────────────────────────────
   STATUS BAR (shared across all 3 steps)
   ──────────────────────────────────────────────── */

export const StatusBarWrapper = styled.div`
  display: flex;
  align-items: center;
  border-radius: 14px;
  background: linear-gradient(90deg, #2E1900 0%, #442600 100%);
  box-shadow: 0 0 10px 0px rgba(255, 89, 89, 0.4);
  padding: 8px 12px;
  margin: 24px auto 0;
  width: 90%;
  max-width: 300px;
  gap: 10px;
`;

export const StatusBarIcon = styled.div`
  color: ${WHITE};
  font-size: 14px;
  display: flex;
  align-items: center;

  svg {
    width: 16px;
    height: 16px;

    path {
      fill: ${WHITE} !important;
    }
  }
`;

export const StatusBarText = styled.span`
  color: ${WHITE};
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.01em;
`;

/**
 * Small square "stop" indicator on the right of the status bar,
 * matching the Figma (not a pulsing dot — a small rounded square).
 */
export const StatusBarIndicator = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;

  .inner-square {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background: ${WHITE};
    opacity: 0.9;
  }
`;

/* ────────────────────────────────────────────────
   BUS variant — kept from original
   ──────────────────────────────────────────────── */

export const BusInfo = styled(AirportInfo)`
  padding: 0 5px;
`;

export const BusItem = styled(AirportItem)`
  h1.ant-typography {
    line-height: 35px;
    font-size: 28px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

export const Container = styled.div``;

export const InfoTextWrapper = styled.div`
  width: 185px;
  margin: 0 auto;
`;

export const ImageWrapper = styled.div`
  text-align: center;
  margin-bottom: 20px;
  position: relative;
`;

export const TerminalLabel = styled(StyledParagraph)`
  @supports (-webkit-line-clamp: 1) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
  }
`;
